import type { components } from "@/lib/api/schema.gen";

import { EMPTY_PROFILE, type EnterpriseProfileDetail, type ProfileFormValues } from "./types";

/**
 * Profile fields that must be present before the diagnostic can start. The
 * enterprise profile is a *gate*, not a wizard step: we only interrupt the
 * journey when one of these is missing (see ProfileGate), never on every visit.
 */
export const REQUIRED_FIELDS: readonly (keyof ProfileFormValues)[] = [
  "name",
  "enterprise_type",
  "address",
  "city",
  "zip_code",
  "phone_number",
];

/**
 * The address parts the server canonicalises on save.
 *
 * What gets stored is the address Google confirmed, not the one that was
 * typed, so a client that keeps its own version re-sends a value that no
 * longer matches the stored one — and the API, seeing a changed address,
 * spends a billed lookup on every debounced save. Adopting these back is what
 * keeps that guard working.
 */
export const CANONICAL_ADDRESS_FIELDS = [
  "address",
  "city",
  "zip_code",
] as const satisfies readonly (keyof ProfileFormValues)[];

/** Map a loaded profile onto the editable form shape. */
export function toValues(profile: EnterpriseProfileDetail): ProfileFormValues {
  return {
    // `display_name` first: for a training participant `name` holds the
    // fabricated value OAC-464 keeps out of every interface, and seeding the
    // edit form from it would show them "formation-<hex>" as their own company.
    name: profile.display_name || profile.name || "",
    enterprise_type: profile.enterprise_type ?? "",
    address: profile.address ?? "",
    city: profile.city ?? "",
    zip_code: profile.zip_code ?? "",
    phone_number: profile.phone_number ?? "",
    website: profile.website ?? "",
    email: profile.email ?? "",
    description: profile.description ?? "",
    sector_ids: profile.sector_ids ?? [],
    characteristic_ids: profile.characteristic_ids ?? [],
    company_size_id: profile.company_size?.id ?? "",
  };
}

/** Map the editable form shape onto a PATCH body. */
export function toPatch(
  values: ProfileFormValues,
): components["schemas"]["PatchedEnterpriseProfileDetail"] {
  return {
    name: values.name,
    enterprise_type:
      values.enterprise_type as EnterpriseProfileDetail["enterprise_type"],
    address: values.address,
    city: values.city,
    zip_code: values.zip_code,
    phone_number: values.phone_number,
    website: values.website,
    email: values.email,
    description: values.description,
    sector_ids: values.sector_ids,
    characteristic_ids: values.characteristic_ids,
    company_size_id: values.company_size_id || null,
  };
}

/** Required fields that are still empty on this profile. */
export function missingRequired(
  profile: EnterpriseProfileDetail,
): (keyof ProfileFormValues)[] {
  const values = toValues(profile);
  return REQUIRED_FIELDS.filter((field) => !String(values[field] ?? "").trim());
}

/**
 * Sort a 400's field errors into the ones a surface can show and the ones it
 * cannot.
 *
 * `toApiError` folds *every* key of the response body into `fieldErrors`,
 * including `non_field_errors` and fields a given form does not display. A
 * surface that renders only what it knows and stays quiet about the rest turns
 * those into a silently failed save — no message, no toast. So the leftovers
 * come back here to be said out loud instead.
 *
 * `renderable` is the set of keys the calling surface actually puts on screen.
 */
export function splitFieldErrors(
  fieldErrors: Record<string, string[]> | undefined,
  renderable: ReadonlySet<string>,
): { rendered: Record<string, string>; unrendered: string[] } {
  const rendered: Record<string, string> = {};
  const unrendered: string[] = [];
  for (const [key, messages] of Object.entries(fieldErrors ?? {})) {
    const text = messages.join(" ");
    if (renderable.has(key)) {
      rendered[key] = text;
    } else {
      unrendered.push(text);
    }
  }
  return { rendered, unrendered };
}

/**
 * The keys `ProfileFields` renders an error for — the `renderable` set for the
 * two surfaces that use it (`ProfileEditor`, `CreateProfileGate`).
 *
 * This is deliberately *not* `Object.keys(EMPTY_PROFILE)`. That is the form's
 * data shape, which is a different thing from what the component puts on
 * screen, and claiming a key the component does not draw suppresses the toast
 * for an error nothing then displays — a save that fails in silence. Every key
 * here must have a matching error element in `ProfileFields`;
 * `ProfileFields.test.tsx` fails if one drifts out.
 */
export const PROFILE_FIELD_KEYS: ReadonlySet<string> = new Set([
  "name",
  "enterprise_type",
  "description",
  "address",
  "city",
  "zip_code",
  "phone_number",
  "website",
  "email",
  "company_size_id",
  "sector_ids",
  "characteristic_ids",
]);

export { EMPTY_PROFILE };
